// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — earcut has no type declarations
import earcut from 'earcut'
import { generate, correction, mode } from 'lean-qr'

// Physical dimensions (mm)
const MODEL_SIZE_MM = 50
const BASE_HEIGHT_MM = 1.5
const MODULE_HEIGHT_MM = 1.5

// Shared constants with stencilExport.ts — keep in sync.
export const BRIDGE_MM = 1.0           // width of bridge across finder eye rings
export const DOT_RADIUS_RATIO = 0.4    // legacy: kept for stencilExport import compat
export const DOT_FILL_RATIO = 0.85     // data module square side as fraction of module size
export const DOT_CORNER_RATIO = 0.15   // corner radius as fraction of module size
export const CIRCLE_SEGS = 24
export const ARC_SEGS_PER_TURN = 48   // tessellation density for ring arcs

// ---------------------------------------------------------------------------
// Shared type
// ---------------------------------------------------------------------------

export type Contour = [number, number][]

// Minimal interface over lean-qr's Bitmap2D — also enables test fakes.
export interface QrMatrix {
  readonly size: number
  get(x: number, y: number): boolean
}

// ---------------------------------------------------------------------------
// Shared 2D geometry helpers (also used by stencilExport.ts)
// ---------------------------------------------------------------------------

export function circle(cx: number, cy: number, r: number): Contour {
  return Array.from({ length: CIRCLE_SEGS }, (_, i) => {
    const a = (2 * Math.PI * i) / CIRCLE_SEGS
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number, number]
  })
}

// Arc polyline around (cx, cy) from angle a0 to a1, endpoints included.
export function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number): Contour {
  const segs = Math.max(2, Math.ceil((Math.abs(a1 - a0) / (2 * Math.PI)) * ARC_SEGS_PER_TURN))
  return Array.from({ length: segs + 1 }, (_, i) => {
    const a = a0 + ((a1 - a0) * i) / segs
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number, number]
  })
}

// Single C-shaped contour for a rounded-square ring (band 1 module wide,
// outer corner radius R, inner corner radius R − m). One bridge of width
// BRIDGE_MM sits on the top straight section at `cutPos` from the left corner.
// Local Y-down coordinates with the ring's top-left at (0,0); side length s.
export function roundedRingContour(s: number, m: number, R: number, cutPos: number): Contour {
  const g = BRIDGE_MM / 2
  const ri = R - m

  const outerArc = (cx: number, cy: number, a0: number, a1: number) =>
    arcPoints(cx, cy, R, a0, a1)
  const innerArc = (cx: number, cy: number, a0: number, a1: number) =>
    ri > 0
      ? arcPoints(cx, cy, ri, a0, a1)
      : ([[cx + ri * Math.cos(a1), cy + ri * Math.sin(a1)]] as Contour)

  return [
    // --- outer perimeter (CW in Y-down) ---
    [cutPos + g, 0],
    ...outerArc(s - R, R,  -Math.PI / 2, 0),
    ...outerArc(s - R, s - R, 0, Math.PI / 2),
    ...outerArc(R,     s - R, Math.PI / 2, Math.PI),
    ...outerArc(R,     R,     Math.PI, 1.5 * Math.PI),
    [cutPos - g, 0],
    // --- step inward at the gap ---
    [cutPos - g, m],
    // --- inner perimeter CCW (back toward gap right) ---
    ...innerArc(R,     R,     -Math.PI / 2, -Math.PI),
    ...innerArc(R,     s - R, Math.PI, Math.PI / 2),
    ...innerArc(s - R, s - R, Math.PI / 2, 0),
    ...innerArc(s - R, R,     0, -Math.PI / 2),
    [cutPos + g, m],
  ]
}

// A square with rounded corners (corner radius rc), top-left at (x0, y0).
export function roundedSquare(x0: number, y0: number, size: number, rc: number): Contour {
  const x1 = x0 + size
  const y1 = y0 + size
  return [
    ...arcPoints(x1 - rc, y0 + rc, rc, -Math.PI / 2, 0),
    ...arcPoints(x1 - rc, y1 - rc, rc, 0, Math.PI / 2),
    ...arcPoints(x0 + rc, y1 - rc, rc, Math.PI / 2, Math.PI),
    ...arcPoints(x0 + rc, y0 + rc, rc, Math.PI, 1.5 * Math.PI),
  ]
}

// Rounded-square data module centred at (cx, cy) within a module cell of size m.
// Side = m * DOT_FILL_RATIO, corner radius = m * DOT_CORNER_RATIO.
export function dataModuleSquare(cx: number, cy: number, m: number): Contour {
  const side = m * DOT_FILL_RATIO
  const rc = m * DOT_CORNER_RATIO
  return roundedSquare(cx - side / 2, cy - side / 2, side, rc)
}

// ---------------------------------------------------------------------------
// Contour winding helpers — shared with stencilExport.ts
// ---------------------------------------------------------------------------

export function signedArea(contour: Contour): number {
  let area = 0
  for (let i = 0; i < contour.length; i++) {
    const [x1, y1] = contour[i]
    const [x2, y2] = contour[(i + 1) % contour.length]
    area += x1 * y2 - x2 * y1
  }
  return area / 2
}

// extrudeContours expects: outer contour with positive shoelace area,
// hole contours with negative area (wall normals depend on winding).
export function withWinding(contour: Contour, positive: boolean): Contour {
  const area = signedArea(contour)
  if ((area > 0) !== positive) return [...contour].reverse()
  return contour
}

// ---------------------------------------------------------------------------
// Extrude a polygon (with optional holes) into an STL prism.
// Uses earcut for triangulation. Input contours are in Y-down coordinates.
// Contour 0 is the outer boundary (positive shoelace area); the rest are holes
// (negative shoelace area) — winding matters for wall triangle orientation.
// Shared with stencilExport.ts.
// ---------------------------------------------------------------------------

export function extrudeContours(
  view: DataView,
  offset: number,
  contours: Contour[],
  z0: number,
  z1: number,
): number {
  if (contours.length === 0 || contours[0].length < 3) return offset

  // Flatten for earcut
  const verts: number[] = []
  const holes: number[] = []
  for (let c = 0; c < contours.length; c++) {
    if (c > 0) holes.push(verts.length / 2)
    for (const [x, y] of contours[c]) verts.push(x, y)
  }

  const idx = earcut(verts, holes.length > 0 ? holes : undefined) as number[]
  if (idx.length === 0) return offset

  for (let i = 0; i < idx.length; i += 3) {
    const ax = verts[idx[i]   * 2], ay = verts[idx[i]   * 2 + 1]
    const bx = verts[idx[i+1] * 2], by = verts[idx[i+1] * 2 + 1]
    const cx = verts[idx[i+2] * 2], cy = verts[idx[i+2] * 2 + 1]
    offset = writeTriangle(view, offset, 0,0,+1, ax,ay,z1, bx,by,z1, cx,cy,z1)
    offset = writeTriangle(view, offset, 0,0,-1, ax,ay,z0, cx,cy,z0, bx,by,z0)
  }

  // Side walls — one quad per edge per contour
  for (const contour of contours) {
    const n = contour.length
    for (let i = 0; i < n; i++) {
      const [ax, ay] = contour[i]
      const [bx, by] = contour[(i + 1) % n]
      const ex = bx - ax, ey = by - ay
      const len = Math.sqrt(ex * ex + ey * ey) || 1
      const nx = ey / len, ny = -ex / len
      offset = writeTriangle(view, offset, nx, ny, 0, ax, ay, z0, bx, by, z0, bx, by, z1)
      offset = writeTriangle(view, offset, nx, ny, 0, ax, ay, z0, bx, by, z1, ax, ay, z1)
    }
  }

  return offset
}

export function countExtrudeTris(contours: Contour[]): number {
  if (contours.length === 0 || contours[0].length < 3) return 0
  const verts: number[] = []
  const holes: number[] = []
  for (let c = 0; c < contours.length; c++) {
    if (c > 0) holes.push(verts.length / 2)
    for (const [x, y] of contours[c]) verts.push(x, y)
  }
  const idx = earcut(verts, holes.length > 0 ? holes : undefined) as number[]
  const capTris = (idx.length / 3) * 2
  const sideTris = contours.reduce((s, c) => s + c.length, 0) * 2
  return capTris + sideTris
}

// ---------------------------------------------------------------------------
// Binary STL helpers
// ---------------------------------------------------------------------------

function writeFloat32(view: DataView, offset: number, value: number): number {
  view.setFloat32(offset, value, true)
  return offset + 4
}

function writeVec3(view: DataView, offset: number, x: number, y: number, z: number): number {
  offset = writeFloat32(view, offset, x)
  offset = writeFloat32(view, offset, y)
  offset = writeFloat32(view, offset, z)
  return offset
}

function writeTriangle(
  view: DataView, offset: number,
  nx: number, ny: number, nz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number {
  offset = writeVec3(view, offset, nx, ny, nz)
  offset = writeVec3(view, offset, ax, ay, az)
  offset = writeVec3(view, offset, bx, by, bz)
  offset = writeVec3(view, offset, cx, cy, cz)
  view.setUint16(offset, 0, true)
  return offset + 2
}

// ---------------------------------------------------------------------------
// URL encoding — shared logic with stencilExport.ts
// ---------------------------------------------------------------------------

// Uppercases the full URL so lean-qr uses alphanumeric mode (5.5 bits/char
// vs 8 for byte), keeping version 1 (21×21) feasible at EC level L.
// Identical to toStencilUrl() — both exports encode the same data.
export function toPlaqueUrl(galleryUrl: string): string {
  return galleryUrl.toUpperCase()
}

// ---------------------------------------------------------------------------
// Build plaque contours from a QR matrix.
//
// The plaque is the positive of the stencil: where the stencil has holes,
// the plaque has raised modules. Both use identical geometry (same circle
// radius, same C-shaped finder ring with bridge, same rounded center square)
// so the two models are true inverses of each other.
//
// Returns one Contour[] per raised shape (each array has exactly one element —
// no holes — since raised additive geometry can never create islands).
// All contours are in Y-up mm space (Y-flip applied), positive winding.
// ---------------------------------------------------------------------------

export function buildPlaqueContours(matrix: QrMatrix): Contour[][] {
  const n = matrix.size
  const m = MODEL_SIZE_MM / n

  const finderOrigins: [number, number][] = [
    [0, 0],
    [n - 7, 0],
    [0, n - 7],
  ]
  const inFinder = (x: number, y: number) =>
    finderOrigins.some(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7)

  const shapes: Contour[] = []

  // Data modules outside finder eyes → rounded squares filling most of the cell.
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (!matrix.get(x, y) || inFinder(x, y)) continue
      shapes.push(dataModuleSquare((x + 0.5) * m, (y + 0.5) * m, m))
    }
  }

  // Finder eyes → same C-shaped ring + rounded-square center as the stencil.
  // Raised geometry has no island constraint, but we use the identical contours
  // so the two models are exact geometric inverses.
  for (const [fx, fy] of finderOrigins) {
    const ox = fx * m
    const oy = fy * m
    const translate = (c: Contour): Contour => c.map(([x, y]) => [ox + x, oy + y])
    shapes.push(translate(roundedRingContour(7 * m, m, 1.5 * m, 2 * m)))
    shapes.push(roundedSquare(ox + 2 * m, oy + 2 * m, 3 * m, m))
  }

  // Flip Y: matrix is Y-down, STL viewed from +Z is Y-up.
  const flipY = (c: Contour): Contour => c.map(([x, y]) => [x, MODEL_SIZE_MM - y])

  // Each shape is its own raised solid (no holes), positive winding.
  return shapes.map((s) => [withWinding(flipY(s), true)])
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function generateStl(url: string): ArrayBuffer {
  const matrix = generate(mode.alphaNumeric(url), {
    minCorrectionLevel: correction.L,
    maxCorrectionLevel: correction.L,
    maxVersion: 1,
  })

  const moduleGroups = buildPlaqueContours(matrix)

  const s = MODEL_SIZE_MM
  const z0 = BASE_HEIGHT_MM
  const z1 = BASE_HEIGHT_MM + MODULE_HEIGHT_MM

  // Backplate: solid box (12 triangles) + raised module solids on top.
  const baseTris = 12
  const shapeTris = moduleGroups.reduce((sum, cs) => sum + countExtrudeTris(cs), 0)
  const totalTris = baseTris + shapeTris

  const buffer = new ArrayBuffer(80 + 4 + totalTris * 50)
  const view = new DataView(buffer)

  const header = 'QR STL generated by h3r.no'
  for (let i = 0; i < Math.min(header.length, 80); i++) view.setUint8(i, header.charCodeAt(i))
  view.setUint32(80, totalTris, true)

  let off = 84

  // Bottom (-Z)
  off = writeTriangle(view, off, 0,0,-1, 0,0,0, 0,s,0, s,0,0)
  off = writeTriangle(view, off, 0,0,-1, s,0,0, 0,s,0, s,s,0)
  // Front (-Y)
  off = writeTriangle(view, off, 0,-1,0, 0,0,0, s,0,0, s,0,BASE_HEIGHT_MM)
  off = writeTriangle(view, off, 0,-1,0, 0,0,0, s,0,BASE_HEIGHT_MM, 0,0,BASE_HEIGHT_MM)
  // Back (+Y)
  off = writeTriangle(view, off, 0,+1,0, 0,s,0, s,s,BASE_HEIGHT_MM, s,s,0)
  off = writeTriangle(view, off, 0,+1,0, 0,s,0, 0,s,BASE_HEIGHT_MM, s,s,BASE_HEIGHT_MM)
  // Left (-X)
  off = writeTriangle(view, off, -1,0,0, 0,0,0, 0,0,BASE_HEIGHT_MM, 0,s,0)
  off = writeTriangle(view, off, -1,0,0, 0,s,0, 0,0,BASE_HEIGHT_MM, 0,s,BASE_HEIGHT_MM)
  // Right (+X)
  off = writeTriangle(view, off, +1,0,0, s,0,0, s,s,0, s,0,BASE_HEIGHT_MM)
  off = writeTriangle(view, off, +1,0,0, s,s,0, s,s,BASE_HEIGHT_MM, s,0,BASE_HEIGHT_MM)
  // Backplate top (+Z)
  off = writeTriangle(view, off, 0,0,+1, 0,0,z0, s,0,z0, s,s,z0)
  off = writeTriangle(view, off, 0,0,+1, 0,0,z0, s,s,z0, 0,s,z0)

  // Raised QR shapes
  for (const cs of moduleGroups) {
    off = extrudeContours(view, off, cs, z0, z1)
  }

  return buffer
}

export function downloadStl(url: string, filename = 'qr-code'): void {
  const buffer = generateStl(toPlaqueUrl(url))
  const blob = new Blob([buffer], { type: 'model/stl' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = `${filename}.stl`
  a.click()
  URL.revokeObjectURL(href)
}

// ---------------------------------------------------------------------------
// DXF export — flat 2D profile for CAD import + extrude
//
// Writes all QR module contours as closed LWPOLYLINE entities in a minimal
// DXF R12 file. Each 4-connected blob of dark modules is one closed polyline
// (outer boundary). Finder eyes produce two polylines each (ring outline +
// centre square). Import into Fusion 360, SolidWorks, Rhino, FreeCAD etc.
// and use the extrude tool.
//
// Units: mm. Origin at bottom-left of QR area.
// ---------------------------------------------------------------------------

function dxfLwpolyline(contour: Contour): string {
  const lines: string[] = [
    '  0\nLWPOLYLINE',
    '  8\n0',
    ' 70\n1',
    ' 90\n' + contour.length,
  ]
  for (const [x, y] of contour) {
    lines.push(' 10\n' + x.toFixed(6))
    lines.push(' 20\n' + y.toFixed(6))
  }
  return lines.join('\n')
}

export function generateDxf(url: string): string {
  const matrix = generate(mode.alphaNumeric(url), {
    minCorrectionLevel: correction.L,
    maxCorrectionLevel: correction.L,
    maxVersion: 1,
  })

  const groups = buildPlaqueContours(matrix)
  const contours: Contour[] = groups.flatMap((g) => g)
  const entities = contours.map(dxfLwpolyline).join('\n')

  return [
    '  0\nSECTION',
    '  2\nHEADER',
    '  0\nENDSEC',
    '  0\nSECTION',
    '  2\nENTITIES',
    entities,
    '  0\nENDSEC',
    '  0\nEOF',
  ].join('\n')
}

export function downloadDxf(url: string, filename = 'qr-code'): void {
  const dxf = generateDxf(toPlaqueUrl(url))
  const blob = new Blob([dxf], { type: 'application/dxf' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = `${filename}.dxf`
  a.click()
  URL.revokeObjectURL(href)
}
